/** How we can show a file inside the app (modal). */
export type AttachmentPreviewKind = 'image' | 'pdf' | 'office' | 'text' | 'video' | 'audio' | 'none';

/** Last path segment without query string (works for Azure SAS URLs). */
export function fileNameFromAttachmentUrl(fileUrl: string): string {
  try {
    const path = new URL(fileUrl, typeof window !== 'undefined' ? window.location.origin : undefined).pathname;
    const seg = path.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(seg) || 'file';
  } catch {
    const raw = fileUrl.split('/').pop() || 'file';
    return decodeURIComponent(raw.split('?')[0] || raw);
  }
}

/** Prefer display name; fall back to URL path so extensions survive ?sas tokens on the query string. */
export function resolveAttachmentLabel(fileName: string, fileUrl: string): string {
  const clean = (fileName || '').trim().split('?')[0];
  if (clean && clean.includes('.') && !clean.startsWith('http')) return clean;
  return fileNameFromAttachmentUrl(fileUrl);
}

export function getAttachmentPreviewKind(fileName: string, fileUrl?: string): AttachmentPreviewKind {
  const label = fileUrl ? resolveAttachmentLabel(fileName, fileUrl) : fileName.split('?')[0];
  const ext = label.split('.').pop()?.toLowerCase() || '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return 'office';
  if (['txt', 'csv', 'json', 'xml', 'log', 'md', 'tsv'].includes(ext)) return 'text';
  if (['mp4', 'webm', 'mov', 'm4v', 'ogv', 'mkv', 'avi', 'mts', 'm2ts'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'opus', 'wma'].includes(ext)) return 'audio';
  return 'none';
}

/** Office files we can parse in the browser (no third-party viewer URL required). */
export function officeFileSupportsLocalPreview(fileName: string, fileUrl?: string): 'docx' | 'sheet' | false {
  const label = fileUrl ? resolveAttachmentLabel(fileName, fileUrl) : fileName.split('?')[0];
  const ext = label.split('.').pop()?.toLowerCase() || '';
  if (ext === 'docx') return 'docx';
  if (ext === 'xls' || ext === 'xlsx') return 'sheet';
  return false;
}

/** Microsoft Office Online embed — URL must be reachable by Microsoft servers (e.g. SAS blob URL). */
export function officeEmbedViewerUrl(fileUrl: string): string {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}

/** Optional fallback when Office viewer fails (also requires a publicly fetchable URL). */
export function googleDocsViewerEmbedUrl(fileUrl: string): string {
  return `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
}
